import { useEffect, useContext, useMemo } from 'react'
import { useWeb3React } from '@web3-react/core'
import { StoreAction, StoreContext } from 'web/lib/store'
import {
  GawdsContractFactory,
} from 'web/lib/contract'
import { Power } from 'web/lib/gawd'
import { CultBinder, Rebaser } from 'web/types/ethers-contracts'
import useSWR from 'swr'
import { CacheKey } from 'web/lib/cache-keys'
import useBoundCult from './useBoundCult'

export default function useRebaserData() {
  const { active, account } = useWeb3React()

  const rebaserContract: Rebaser = GawdsContractFactory.get("Rebaser") as Rebaser
  const cultsContract: CultBinder = GawdsContractFactory.get("CultBinder") as CultBinder

  const {
    cultGame: [cultGame, dispatchCult],
  } = useContext(StoreContext)

  const precision = 1000

  // Load Cult population data
  const { data: cultStats } =
    useSWR(CacheKey.cultPopulation(), async () => {
      const res = await rebaserContract.getAllCultPopulationsAndBoosts()

      const populations = res[0];
      const boosts = res[1].map((a) => a.toNumber() / precision)
      const gawdCounts = (await rebaserContract.getGawdsCountForCults()).map((c) => c.toNumber())

      const stats = {
        1: { populationTotal: populations[1], rewardBoost: boosts[1], gawdTotal: gawdCounts[1] },
        2: { populationTotal: populations[2], rewardBoost: boosts[2], gawdTotal: gawdCounts[2] },
        3: { populationTotal: populations[3], rewardBoost: boosts[3], gawdTotal: gawdCounts[3] },
      }
      return stats
    }, {
      refreshInterval: 1000 * 60 * 30,
    })

  const currentCult = useBoundCult()
  // Set the cult boost amount
  const currentCultBoost = useMemo(() => {
    if (!currentCult || !cultStats) {
      return 0
    }

    return cultStats[currentCult.id].rewardBoost
  }, [cultStats, currentCult])

  const { error } = useSWR(active ? CacheKey.rebaserData(account) : null, async () => {
    const baseEnergyPerGawd = (await rebaserContract.bloodPerGawd()).toNumber()
    const earlyAccessBoost = (await rebaserContract.currentYield()).toNumber() / precision
    const alignedPower = Power.getById((await rebaserContract.alignedPower()))
    if (!alignedPower) {
      throw new Error("No aligned power is set")
    }
    const alignedPowerBoost = (await rebaserContract.alignedPowerBoost()).toNumber() / precision

    const [ totalHealth, happyGawdsCount, alignedGawdsCount ] = await rebaserContract.getOverallHealthAndAlignedPowersCount(account, alignedPower.id)
    const globalGawdHealth = totalHealth.toNumber() / precision

    dispatchCult([StoreAction.Update, {
      earlyAccessBoost: earlyAccessBoost,
      baseEnergyPerGawd: baseEnergyPerGawd,
      happyGawds: happyGawdsCount,
      globalGawdHealth: globalGawdHealth,
      alignedPower: alignedPower,
      alignedPowerBoost: alignedPowerBoost,
      alignedGawdsByPowerCount: alignedGawdsCount,
      alignedPowerEarnings: alignedGawdsCount * alignedPowerBoost * baseEnergyPerGawd * globalGawdHealth
    }])

    dispatchCult([StoreAction.Update, { boundAt: (await cultsContract.ownersBoundAt(account)).toNumber() }])
    dispatchCult([StoreAction.Update, { lastRebaseBlock: (await rebaserContract.lastRebaseBlock()).toNumber() }])
    dispatchCult([StoreAction.Update, { nextRebaseBlock: (await rebaserContract.nextRebaseBlock()).toNumber() }])

    return {
      // TODO return all the above data instead and remove from global store
    }
  }, {
    refreshInterval: 1000 * 60 * 15
  })

  return {
    cultStats: cultStats || { 1: {}, 2: {}, 3: {} },
    currentCultBoost: currentCultBoost || 0,
    error: error
  }
}
